import { lowerFirst } from 'lodash';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '@/shared/context/context';
import signalRMessages from '@/redux/actions/signalRMessages';
import { trpc } from '@/config/trpc';
import useNotification from '@/shared/hooks/UseNotification';
import useSocketEvent from '@/shared/hooks/useSocketEvent';

const useGlobalManualProcessingOfAdditionalPerformancesMessages = () => {
  const { currentTeamUser } = useContext(AppContext);
  const { sendNotification } = useNotification();
  const { t } = useTranslation();
  const trpcUtils = trpc.useUtils();

  const invalidateInvoicingInProgress = (homeownerAssociationId?: string) => {
    if (homeownerAssociationId) {
      trpcUtils.hoa.standardPerformances.isInvoicingInProgress.invalidate({
        homeownerAssociationId,
      });
      return;
    }

    trpcUtils.hoa.standardPerformances.isInvoicingInProgress.invalidate();
  };

  const invalidatePerformances = (homeownerAssociationId?: string) => {
    invalidateInvoicingInProgress(homeownerAssociationId);

    if (homeownerAssociationId) {
      trpcUtils.hoa.performance.list.invalidate({ homeownerAssociationId });
      return;
    }

    trpcUtils.hoa.performance.list.invalidate();
  };

  useSocketEvent(
    signalRMessages.ManualProcessingOfAdditionalPerformancesStarted,
    (userId: string, homeownerAssociationId: string) => {
      if (userId !== currentTeamUser?.userId) return;

      invalidateInvoicingInProgress(homeownerAssociationId);
    },
  );

  useSocketEvent(
    signalRMessages.ManualProcessingOfAdditionalPerformancesCompleted,
    (userId: string) => {
      if (userId !== currentTeamUser?.userId) return;

      sendNotification({
        message: t('_globalMessages.manualProcessingOfAdditionalPerformancesCompleted'),
        options: {
          autoHideDuration: 10000,
        },
        variant: 'success',
      });

      invalidatePerformances();
    },
  );

  useSocketEvent(
    signalRMessages.ManualProcessingOfAdditionalPerformancesFailed,
    (failureReason: string, userId: string, homeownerAssociationId: string) => {
      if (userId !== currentTeamUser?.userId) return;

      sendNotification({
        message: t('_globalMessages.manualProcessingOfAdditionalPerformancesFailed', {
          reason: t(`_performance.failureReasons.${lowerFirst(failureReason)}`),
        }),
        options: {
          autoHideDuration: 10000,
        },
        variant: 'error',
      });
      invalidateInvoicingInProgress(homeownerAssociationId);
      invalidatePerformances(homeownerAssociationId);
    },
  );
};

export default useGlobalManualProcessingOfAdditionalPerformancesMessages;
